import React, { useState } from 'react';
import { WithDefaultLayout } from "@/components/DefautLayout";
import { Title } from "@/components/Title";
import { Page } from "@/types/Page";
import { Table, Modal } from 'antd';
import { BackendApiUrl } from '@/functions/BackendApiUrl';
import { useSwrFetcherWithAccessToken } from '@/functions/useSwrFetcherWithAccessToken';
import useSWR from 'swr';
import { useFetchWithAccessToken } from '@/functions/useFetchWithAccessToken';
import { Authorize } from '@/components/Authorize';
import { License } from '@/interfaces/license-status/License';

const LicenseStatus = () => {
    const swrFetcher = useSwrFetcherWithAccessToken();
    const { data } = useSWR<License[]>(`${BackendApiUrl.getLicenses}`, swrFetcher);

    const { fetchGET } = useFetchWithAccessToken();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [licenseHistory, setLicenseHistory] = useState<License[]>([]);

    const showModal = async (licenseId: number) => {
        try {
            const response = await fetchGET<License[]>(`${BackendApiUrl.getLicenseHistories}/${licenseId}`);
            if (response.data) {
                setLicenseHistory(response.data.map(transformHistory));
            } else {
                console.error('Error fetching license history', response.problem);
            }
        } catch (error) {
            console.error("Error fetching license history", error);
        }
        setIsModalVisible(true);
    };

    const handleOk = () => {
        setIsModalVisible(false);
    };

    const handleCancel = () => {
        setIsModalVisible(false);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
        },
        {
            title: 'License Product',
            dataIndex: 'licenseProduct',
            key: 'licenseProduct',
            render: (text, record) => (
                <a className='text-[#014AB9] font-bold' onClick={() => showModal(record.id)}>
                    {text}
                </a>
            ),
        },
        {
            title: 'License Key',
            dataIndex: 'licenseKey',
            key: 'licenseKey',
            render: text => <a className='text-[#014AB9] font-bold'>{text}</a>,
        },
        {
            title: 'Issue Date',
            dataIndex: 'issueDate',
            key: 'issueDate',
        },
        {
            title: 'Expiry Date',
            dataIndex: 'expiryDate',
            key: 'expiryDate',
        },
        {
            title: 'Days Remain',
            dataIndex: 'daysRemain',
            key: 'daysRemain',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: status => {
                let color;
                if (status === 'Valid') {
                    color = 'text-green-400';
                } else if (status === 'Revoked') {
                    color = 'text-black';
                } else {
                    color = 'text-red-400';
                }
                
                return (
                    <span className={`font-bold ${color}`}>
                        {status.toUpperCase()}
                    </span>
                );
            },
        }
    ];

    const transformedData = data?.map(license => ({
        key: license.licenseId,
        id: license.licenseId,
        licenseProduct: license.licenseProduct,
        licenseKey: license.licenseKey,
        issueDate: license.issueDate ? new Date(license.issueDate).toLocaleDateString() : '-',
        expiryDate: license.expiryDate ? new Date(license.expiryDate).toLocaleDateString() : '-',
        daysRemain: license.daysRemain !== undefined ? `${license.daysRemain} Days` : '-',
        status: !license.isValid && license.daysRemain > 0 ? 'Revoked' : license.isValid ? 'Valid' : 'Expired',
    })) || [];

    const transformHistory = (license) => ({
        ...license,
        key: license.licenseId,
        id: license.licenseId,
        issueDate: license.issueDate ? new Date(license.issueDate).toLocaleDateString() : '-',
        expiryDate: license.expiryDate ? new Date(license.expiryDate).toLocaleDateString() : '-',
        daysRemain: license.daysRemain !== undefined ? `${license.daysRemain} Days` : '-',
        status: !license.isValid && license.daysRemain > 0 ? 'Revoked' : license.isValid ? 'Valid' : 'Expired',
    });

    const modalColumns = [
        {
            title: 'LicenseKey',
            dataIndex: 'licenseKey',
            key: 'licenseKey',
        },
        {
            title: 'Issue Date',
            dataIndex: 'issueDate',
            key: 'issueDate',
            render: text => text ? new Date(text).toLocaleDateString() : '-',
        },
        {
            title: 'Expiry Date',
            dataIndex: 'expiryDate',
            key: 'expiryDate',
            render: text => text ? new Date(text).toLocaleDateString() : '-',
        },
        {
            title: 'Days Remain',
            dataIndex: 'daysRemain',
            key: 'daysRemain',
            render: text => text !== undefined ? `${text}` : '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: status => {
                let color;
                if (status === 'Valid') {
                    color = 'text-green-400';
                } else if (status === 'Revoked') {
                    color = 'text-black';
                } else {
                    color = 'text-red-400';
                }
                
                return (
                    <span className={`font-bold ${color}`}>
                        {status.toUpperCase()}
                    </span>
                );
            },
        }
    ];

    return (
        <div className='w-full p-4'>
            <p className='text-2xl font-bold pb-5'>List of License Product</p>
            <Table id="licenseListTable" columns={columns} dataSource={transformedData} pagination={false} />
            <Modal open={isModalVisible} onOk={handleOk} onCancel={handleCancel} width={800} footer={null} centered>
                <p className='text-3xl pb-5 justify-center align-middle text-center font-bold'>History License</p>
                <Table columns={modalColumns} dataSource={licenseHistory} pagination={false} id='licenseListTable' />
            </Modal>
        </div>
    );
}

const LicenseStatusPage: Page = () => {
    return (
        <Authorize>
            <Title>License Status</Title>
            <LicenseStatus />
        </Authorize>
    )
}

LicenseStatusPage.layout = WithDefaultLayout;
export default LicenseStatusPage;
